import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, ScanLine, X, Printer, PackageCheck,
  ChevronDown, ChevronUp, RotateCcw, Camera,
} from "lucide-react";
import { Card, Btn, Input } from "@/components/admin/ui";
import { ScannerModal } from "@/components/admin/ScannerModal";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

/* ─── helpers ─────────────────────────────────────────────────── */
const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};
const cardId = (item) => `${item.reservation_id}::${item.line_item_index}::${item.unit_index}`;
const fmtDate = (s) =>
  s ? new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";

/* ─── StagingCard ─────────────────────────────────────────────── */
const StagingCard = ({ item, isActive, onActivate, onAssign, onUnassign, onCameraScan }) => {
  const [serial, setSerial] = useState("");
  const inputRef = useRef(null);
  const staged = !!item.staged_serial;
  const id = cardId(item);

  /* auto-focus input when this card becomes active */
  useEffect(() => {
    if (isActive && inputRef.current) {
      setSerial("");
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [isActive]);

  const submit = () => {
    const s = serial.trim();
    if (!s) { toast.error("Enter a stroller serial first"); return; }
    onAssign(s);
    setSerial("");
  };

  return (
    <div
      data-testid={`staging-card-${id}`}
      className={`rounded-xl border mb-3 overflow-hidden transition-all duration-200 ${
        staged
          ? "border-emerald-500/40 bg-emerald-500/6"
          : isActive
          ? "border-[#FFD166]/70 bg-[#FFD166]/6 shadow-[0_0_0_1px_rgba(255,209,102,0.18)]"
          : "border-[#FFD166]/18 bg-black/30"
      }`}
    >
      {/* ── main row ── */}
      <button
        className="w-full text-left p-4 flex items-start gap-3"
        onClick={!staged ? onActivate : undefined}
        data-testid={`staging-card-tap-${id}`}
      >
        {/* status icon */}
        <div className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
          staged ? "bg-emerald-500/20" : isActive ? "bg-[#FFD166]/20" : "bg-white/5"
        }`}>
          {staged
            ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            : <PackageCheck className={`w-4 h-4 ${isActive ? "text-[#FFD166]" : "text-[#B9C8D5]/50"}`} />}
        </div>

        {/* content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <span className="font-semibold text-[#F6F1E4] text-sm leading-tight">
              {item.customer_name}
            </span>
            <span className="text-[10px] text-[#B9C8D5] whitespace-nowrap flex-shrink-0">
              {fmtDate(item.delivery_date)}
            </span>
          </div>
          <p className="text-xs text-[#B9C8D5] mt-0.5 leading-snug">
            {item.product_name}
            {item.unit_total > 1 && (
              <span className="ml-1 text-[#FFD166]/70">· {item.unit_number}/{item.unit_total}</span>
            )}
          </p>
          {item.delivery_slot && (
            <p className="text-[11px] text-[#B9C8D5]/70 mt-0.5">{item.delivery_slot}</p>
          )}

          {/* staged serial display */}
          {staged && (
            <div className="flex items-center gap-2 mt-2">
              <span className="font-mono text-xs text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded">
                {item.staged_serial}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); onUnassign(); }}
                className="text-[10px] text-red-400/70 hover:text-red-400 ml-auto flex items-center gap-1"
                data-testid={`staging-undo-${id}`}
              >
                <RotateCcw className="w-3 h-3" /> Undo
              </button>
            </div>
          )}
        </div>
      </button>

      {/* ── serial input (shown when active) ── */}
      {isActive && !staged && (
        <div className="px-4 pb-4" data-testid={`staging-serial-area-${id}`}>
          <p className="text-[11px] text-[#FFD166] mb-2 font-medium">
            Scan or type the stroller's permanent serial
          </p>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="e.g. EGR-NX-00123"
              className="flex-1 font-mono text-sm h-11"
              data-testid={`staging-serial-input-${id}`}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {/* camera scan for serial */}
            <button
              onClick={onCameraScan}
              title="Scan serial with camera"
              className="w-11 h-11 flex-shrink-0 rounded-lg border border-[#FFD166]/30 bg-[#FFD166]/8 flex items-center justify-center hover:bg-[#FFD166]/18 transition-colors"
              data-testid={`staging-camera-serial-${id}`}
            >
              <Camera className="w-5 h-5 text-[#FFD166]" />
            </button>
            <button
              onClick={submit}
              className="h-11 px-4 rounded-lg bg-[#FFD166] text-black text-sm font-bold hover:bg-[#f5c842] transition-colors flex-shrink-0"
              data-testid={`staging-confirm-${id}`}
            >
              Assign
            </button>
          </div>
          <button
            onClick={onActivate}  /* tap again to close */
            className="text-[11px] text-[#B9C8D5]/60 mt-2 flex items-center gap-1"
          >
            <ChevronUp className="w-3 h-3" /> Cancel
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── StagePrep (main page) ───────────────────────────────────── */
export const StagePrep = () => {
  const [day, setDay] = useState(tomorrow());
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, staged: 0 });
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);

  /* scanner state:
     "customer"  — waiting for a reservation QR to be scanned
     "serial"    — waiting for a stroller serial QR to be scanned  */
  const [scannerMode, setScannerMode] = useState(null);

  /* ── fetch ── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/staging?day=${day}`);
      setItems(data.items);
      setSummary({ total: data.total, staged: data.staged });
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed to load staging data");
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  /* ── next unstaged item for a given reservation_id ── */
  const nextUnstaged = (reservationId) =>
    items.find((i) => i.reservation_id === reservationId && !i.staged_serial);

  /* ── scan handler ── */
  const handleScan = useCallback(async (code) => {
    if (scannerMode === "customer") {
      setScannerMode(null);
      // code = reservation_id or short_code
      try {
        const { data } = await axios.get(`${API}/admin/staging/lookup?code=${encodeURIComponent(code)}`);
        const firstUnstaged = data.items.find((i) => !i.staged_serial);
        if (!firstUnstaged) {
          toast.info(`${data.customer_name} — all items already staged`);
          return;
        }
        setActiveId(cardId(firstUnstaged));
        toast.success(`${data.customer_name} — now scan the stroller serial`);
      } catch (e) {
        toast.error(fmtErr(e.response?.data?.detail) || "QR code not recognised");
      }
      return;
    }

    if (scannerMode === "serial") {
      setScannerMode(null);
      // code = stroller serial — assign to current active card
      const active = items.find((i) => cardId(i) === activeId);
      if (!active) { toast.error("No item selected — tap a card first"); return; }
      await doAssign(active, code);
    }
  }, [scannerMode, activeId, items]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* ── assign ── */
  const doAssign = async (item, serial) => {
    try {
      await axios.post(`${API}/admin/staging/assign`, {
        reservation_id: item.reservation_id,
        line_item_index: item.line_item_index,
        unit_index: item.unit_index,
        unit_serial: serial,
      });
      toast.success(`Assigned ${serial} → ${item.customer_name}`);

      // Update local state immediately (optimistic)
      setItems((prev) =>
        prev.map((i) =>
          cardId(i) === cardId(item)
            ? { ...i, staged_serial: serial, staged_at: new Date().toISOString() }
            : i
        )
      );
      setSummary((s) => ({ ...s, staged: s.staged + 1 }));

      // Auto-advance to the next unstaged item in the same reservation
      const next = items.find(
        (i) => i.reservation_id === item.reservation_id
          && cardId(i) !== cardId(item)
          && !i.staged_serial
      );
      setActiveId(next ? cardId(next) : null);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Assignment failed");
    }
  };

  /* ── unassign ── */
  const doUnassign = async (item) => {
    try {
      await axios.post(`${API}/admin/staging/unassign`, {
        reservation_id: item.reservation_id,
        line_item_index: item.line_item_index,
        unit_index: item.unit_index,
      });
      setItems((prev) =>
        prev.map((i) =>
          cardId(i) === cardId(item) ? { ...i, staged_serial: null, staged_at: null } : i
        )
      );
      setSummary((s) => ({ ...s, staged: Math.max(0, s.staged - 1) }));
      toast.success("Assignment cleared");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed to clear");
    }
  };

  /* ── open labels PDF ── */
  const openPdf = () => window.open(`${API}/admin/labels-pdf?day=${day}`, "_blank");

  /* ── progress ring helpers ── */
  const pct = summary.total ? Math.round((summary.staged / summary.total) * 100) : 0;
  const allDone = summary.total > 0 && summary.staged === summary.total;

  /* ── group items by customer for display ── */
  const grouped = items.reduce((acc, item) => {
    const key = item.reservation_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen pb-32" data-testid="stage-prep-page">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#F6F1E4]">Stage Prep</h1>
          <p className="text-sm text-[#B9C8D5] mt-0.5">Assign strollers to reservations before delivery</p>
        </div>

        {/* progress ring */}
        <div className="flex flex-col items-center" data-testid="staging-progress">
          <div className={`text-2xl font-bold ${allDone ? "text-emerald-400" : "text-[#FFD166]"}`}>
            {summary.staged}<span className="text-base font-normal text-[#B9C8D5]">/{summary.total}</span>
          </div>
          <div className="text-[11px] text-[#B9C8D5]">staged</div>
          {allDone && <div className="text-[11px] text-emerald-400 font-semibold mt-0.5">All ready!</div>}
        </div>
      </div>

      {/* ── Date picker + actions ── */}
      <div className="flex gap-2 mb-5 flex-wrap">
        <input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] text-sm rounded-lg px-3 py-2 flex-1 min-w-[160px]"
          data-testid="staging-date-picker"
        />
        <Btn
          variant="ghost"
          onClick={() => setDay(new Date().toISOString().slice(0, 10))}
          className="text-sm"
          data-testid="staging-today-btn"
        >
          Today
        </Btn>
        <Btn
          variant="ghost"
          onClick={() => setDay(tomorrow())}
          className="text-sm"
          data-testid="staging-tomorrow-btn"
        >
          Tomorrow
        </Btn>
        {summary.total > 0 && (
          <Btn
            variant="ghost"
            onClick={openPdf}
            className="text-sm flex items-center gap-1.5"
            data-testid="staging-print-btn"
          >
            <Printer className="w-4 h-4" />
            Print tags
          </Btn>
        )}
      </div>

      {/* ── Progress bar ── */}
      {summary.total > 0 && (
        <div className="mb-5">
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${allDone ? "bg-emerald-400" : "bg-[#FFD166]"}`}
              style={{ width: `${pct}%` }}
              data-testid="staging-progress-bar"
            />
          </div>
          <p className="text-[11px] text-[#B9C8D5] mt-1">{pct}% complete · {summary.total - summary.staged} remaining</p>
        </div>
      )}

      {/* ── Content ── */}
      {loading && (
        <div className="text-center text-[#B9C8D5] py-16" data-testid="staging-loading">
          Loading…
        </div>
      )}

      {!loading && summary.total === 0 && (
        <div className="text-center py-16" data-testid="staging-empty">
          <PackageCheck className="w-10 h-10 text-[#B9C8D5]/30 mx-auto mb-3" />
          <p className="text-[#B9C8D5]">No paid deliveries on {day}</p>
          <p className="text-sm text-[#B9C8D5]/60 mt-1">Try a different date</p>
        </div>
      )}

      {!loading && Object.entries(grouped).map(([resId, resItems]) => {
        const allResStaged = resItems.every((i) => i.staged_serial);
        return (
          <div key={resId} className="mb-2">
            {resItems.map((item) => (
              <StagingCard
                key={cardId(item)}
                item={item}
                isActive={activeId === cardId(item)}
                onActivate={() => setActiveId((cur) => cur === cardId(item) ? null : cardId(item))}
                onAssign={(serial) => doAssign(item, serial)}
                onUnassign={() => doUnassign(item)}
                onCameraScan={() => {
                  setActiveId(cardId(item));
                  setScannerMode("serial");
                }}
              />
            ))}
          </div>
        );
      })}

      {/* ── Floating scan button ── */}
      {!loading && summary.total > 0 && !allDone && (
        <div className="fixed bottom-6 left-0 right-0 flex justify-center z-40 pointer-events-none">
          <button
            onClick={() => setScannerMode("customer")}
            className="pointer-events-auto flex items-center gap-2.5 bg-[#FFD166] text-black font-bold text-base px-7 py-3.5 rounded-full shadow-2xl active:scale-95 transition-transform"
            data-testid="staging-scan-customer-btn"
          >
            <ScanLine className="w-5 h-5" />
            Scan customer tag
          </button>
        </div>
      )}

      {/* ── Scanner modal ── */}
      <ScannerModal
        open={!!scannerMode}
        onClose={() => setScannerMode(null)}
        onResult={handleScan}
        hint={
          scannerMode === "customer"
            ? "Point at the customer's reservation QR tag"
            : "Point at the stroller's permanent serial barcode"
        }
      />
    </div>
  );
};

export default StagePrep;
